import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Globe, Lock, Mail, Pencil, Users } from 'lucide-react';
import { getApiBaseUrl, formatRelativeTime } from '../lib/utils';
import { track } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';
import ImageLightbox from './ImageLightbox';
import MentionTextarea, { MentionText } from './MentionTextarea';

const POST_CHAR_LIMIT = 2500;
const POST_MIN_CHARS = 50;
const COMMENT_CHAR_LIMIT = 1500;
const MAX_IMAGES = 5;
const MAX_VIDEOS = 1;
const DEFAULT_AUDIENCE = { everyone: true, includeCouncil: false, roles: [], unitNumbers: [], unitTypes: [], unitFeatures: [], parkingSpots: [], userIds: [] };
const PROBLEM_REPORT_AUDIENCE = { ...DEFAULT_AUDIENCE, problemReport: true };
const COUNCIL_AUDIENCE = { ...DEFAULT_AUDIENCE, everyone: false, includeCouncil: true };

const AUDIENCE_PRESETS = [
  { key: 'everyone', label: '🌍 Everyone', audience: DEFAULT_AUDIENCE },
  { key: 'problemReport', label: '⚠️ Report a Problem', audience: PROBLEM_REPORT_AUDIENCE },
  { key: 'council', label: '🏛️ Council', audience: COUNCIL_AUDIENCE }
];

const AUDIENCE_DESCRIPTIONS = {
  everyone: 'Everyone can see this post and will be notified about it.',
  problemReport: 'Everyone can see this post, but only the people who can help with the problem will be notified.',
  council: 'Only council members can see this post and will be notified about it.',
  custom: 'Only the people you choose can see this post and will be notified about it.'
};

const isCouncilOnly = (audience) => !audience.everyone && audience.includeCouncil
  && ![audience.roles, audience.unitTypes, audience.unitFeatures, audience.unitNumbers, audience.parkingSpots, audience.userIds]
    .some(values => values.length > 0);

const getAudienceOption = (audience) => {
  if (audience.problemReport) return 'problemReport';
  if (audience.everyone) return 'everyone';
  if (isCouncilOnly(audience)) return 'council';
  return 'custom';
};

const EMPTY_AUDIENCE_STATS = {
  totalResidents: 0,
  owners: 0,
  tenants: 0,
  councilMembers: 0
};

const ROLE_BADGES = {
  owner: { label: 'Owner', className: 'bg-blue-500/20 text-blue-600 dark:text-blue-400' },
  tenant: { label: 'Tenant', className: 'bg-green-500/20 text-green-600 dark:text-green-400' },
  council: { label: 'Council', className: 'bg-purple-500/20 text-purple-600 dark:text-purple-400' },
  manager: { label: 'Manager', className: 'bg-orange-500/20 text-orange-600 dark:text-orange-400' }
};

const RoleBadge = ({ role }) => {
  const config = ROLE_BADGES[role];
  if (!config) return null;

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

const AuthorMenu = ({ author, isOpen, onToggle, onMessage }) => {
  const name = author?.name || 'Unknown';
  const hasDetails = author && (ROLE_BADGES[author.role] || author.unit || onMessage);

  if (!hasDetails) {
    return <span className="min-w-0 text-sm font-medium text-foreground break-words">{name}</span>;
  }

  return (
    <span className="relative min-w-0" data-author-menu>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="text-left text-sm font-medium text-foreground break-words underline decoration-dotted decoration-muted-foreground underline-offset-4 hover:text-primary hover:decoration-solid hover:decoration-primary transition-colors"
      >
        {name}
      </button>
      {isOpen && (
        <div
          role="dialog"
          aria-label={`About ${name}`}
          className="absolute left-0 top-full z-50 mt-1 flex w-56 flex-col items-start gap-2 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg"
        >
          <RoleBadge role={author.role} />
          {author.unit && <span className="text-sm text-muted-foreground">Unit {author.unit}</span>}
          {onMessage && (
            <button
              type="button"
              onClick={onMessage}
              aria-label={`Message ${name}`}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Mail className="w-4 h-4" aria-hidden="true" />
              Message {name.split(' ')[0]}
            </button>
          )}
        </div>
      )}
    </span>
  );
};

const describeAudience = (post, viewerId) => {
  const aud = post.audience;
  if (!aud || aud === 'everyone' || aud.everyone) return null;
  if (aud === 'council') return 'Council';
  if (aud === 'owners') return 'Owners';
  if (typeof aud === 'string') return 'Custom';

  const listed = (values, one, many) => `${values.length === 1 ? one : many} ${values.join(', ')}`;
  const parts = [];
  if (aud.roles?.includes('owner')) parts.push('Owners');
  if (aud.roles?.includes('tenant')) parts.push('Tenants');
  if (aud.unitTypes?.length) parts.push(`${aud.unitTypes.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')} units`);
  if (aud.unitFeatures?.length) parts.push(`Units with ${aud.unitFeatures.join(' + ')}`);
  if (aud.unitNumbers?.length) parts.push(listed(aud.unitNumbers, 'Unit', 'Units'));
  if (aud.parkingSpots?.length) parts.push(listed(aud.parkingSpots, 'Parking spot', 'Parking spots'));
  if (aud.userIds?.length) {
    parts.push(post.audienceUsers?.length
      ? post.audienceUsers.map(u => (viewerId && String(u._id) === String(viewerId) ? 'You' : u.name || 'Unknown')).join(', ')
      : `${aud.userIds.length} resident${aud.userIds.length !== 1 ? 's' : ''}`);
  }
  if (aud.includeCouncil) parts.push('Council');
  return parts.length > 0 ? parts.join(' · ') : 'Custom';
};

const PostAudience = ({ post, viewerId }) => {
  const label = describeAudience(post, viewerId);
  const Icon = label ? Lock : Globe;

  return (
    <div className={`mb-2 flex items-start gap-1.5 text-xs ${label ? 'text-primary' : 'text-muted-foreground'}`}>
      <Icon className="w-3.5 h-3.5 mt-1 shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words">
        <span className="text-muted-foreground">Visible to:</span>{' '}
        <span className="font-medium">{label || (post.audience?.problemReport ? 'Everyone · Problem report' : 'Everyone')}</span>
      </span>
    </div>
  );
};

export function ForumContent({ condo, focusPostId }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { condoSlug } = useParams();
  const { getToken, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [audienceStats, setAudienceStats] = useState(EMPTY_AUDIENCE_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const highlightedPostId = searchParams.get('post');
  const postRefs = useRef({});
  const hasScrolled = useRef(false);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postAudience, setPostAudience] = useState(DEFAULT_AUDIENCE);
  const [audienceOptions, setAudienceOptions] = useState({ unitTypes: [], unitFeatures: [], unitNumbers: [], parkingSpots: [], residents: [] });
  const [unitNumberInput, setUnitNumberInput] = useState('');
  const [parkingSpotInput, setParkingSpotInput] = useState('');
  const [residentInput, setResidentInput] = useState('');
  const [residentNameMap, setResidentNameMap] = useState({});
  const [postImages, setPostImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [postVideos, setPostVideos] = useState([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketSearch, setTicketSearch] = useState('');
  const [showTicketSearch, setShowTicketSearch] = useState(false);
  const [tickets, setTickets] = useState([]);
  const postTextareaRef = useRef(null);
  const commentTextareaRef = useRef(null);
  const [reactionLoading, setReactionLoading] = useState({});
  const [reactionPickerOpen, setReactionPickerOpen] = useState(null);
  const [authorMenuOpen, setAuthorMenuOpen] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editImages, setEditImages] = useState([]);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const [editVideos, setEditVideos] = useState([]);
  const [originalEditVideos, setOriginalEditVideos] = useState([]);
  const [uploadingEditVideo, setUploadingEditVideo] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const editTextareaRef = useRef(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [editCommentImages, setEditCommentImages] = useState([]);
  const [uploadingCommentEditImage, setUploadingCommentEditImage] = useState(false);
  const [editCommentVideos, setEditCommentVideos] = useState([]);
  const [originalEditCommentVideos, setOriginalEditCommentVideos] = useState([]);
  const [uploadingCommentEditVideo, setUploadingCommentEditVideo] = useState(false);
  const [savingCommentEdit, setSavingCommentEdit] = useState(false);
  const [commentImages, setCommentImages] = useState([]);
  const [uploadingCommentImage, setUploadingCommentImage] = useState(false);
  const [commentVideos, setCommentVideos] = useState([]);
  const [uploadingCommentVideo, setUploadingCommentVideo] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [customPanelOpen, setCustomPanelOpen] = useState(false);
  const [draftAudience, setDraftAudience] = useState({ ...DEFAULT_AUDIENCE, everyone: false });
  const [appliedReachCount, setAppliedReachCount] = useState(0);
  const [composerMentionCandidates, setComposerMentionCandidates] = useState(null);
  const [postMentionCandidates, setPostMentionCandidates] = useState({});

  const autoResizeTextarea = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  // Best-effort cleanup for a video that was uploaded (and compressed/stored)
  // but discarded before ever being attached to a saved post/comment —
  // otherwise it's an orphaned object in storage forever. Never call this for
  // a video still attached to a persisted post/comment: the server already
  // handles that deletion itself when a PUT actually removes it.
  const deleteUploadedVideo = async (key) => {
    if (!key) return;
    try {
      await axios.delete(`${getApiBaseUrl()}/api/upload/video`, {
        data: { key },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
    } catch (err) {
      console.error('Failed to delete orphaned video:', err);
    }
  };

  useEffect(() => {
    if (focusPostId) {
      fetchFocusedPost(focusPostId);
    } else if (highlightedPostId) {
      fetchPostsForHighlight(highlightedPostId);
    } else {
      fetchPosts(1);
    }
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condo, focusPostId]);

  useEffect(() => {
    if (!reactionPickerOpen) return;
    const handler = () => setReactionPickerOpen(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [reactionPickerOpen]);

  useEffect(() => {
    if (!authorMenuOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest?.('[data-author-menu]')) setAuthorMenuOpen(null);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setAuthorMenuOpen(null);
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [authorMenuOpen]);

  useEffect(() => {
    if (highlightedPostId && posts.length > 0 && !loading && !hasScrolled.current) {
      const post = posts.find(p => p._id === highlightedPostId);
      if (post) {
        setExpandedPostId(highlightedPostId);
        hasScrolled.current = true;
        setTimeout(() => {
          postRefs.current[highlightedPostId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [highlightedPostId, posts, loading]);

  const fetchMentionCandidates = async (params) => {
    const response = await axios.get(`${getApiBaseUrl()}/api/forum/condo/${condo._id}/mention-candidates`, {
      params,
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return response.data;
  };

  useEffect(() => {
    if (!composerExpanded || !condo?._id) return;
    let cancelled = false;
    setComposerMentionCandidates(null);
    fetchMentionCandidates({ audience: JSON.stringify(postAudience) })
      .then(data => { if (!cancelled) setComposerMentionCandidates(data); })
      .catch(err => console.error('Failed to load mention suggestions:', err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerExpanded, postAudience, condo]);

  useEffect(() => {
    [expandedPostId, editingPostId].filter(id => id && !postMentionCandidates[id]).forEach(postId => {
      fetchMentionCandidates({ postId })
        .then(data => setPostMentionCandidates(prev => ({ ...prev, [postId]: data })))
        .catch(err => console.error('Failed to load mention suggestions:', err));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedPostId, editingPostId]);

  // Backs the focused post page a notification email links to: only the post
  // the reader was notified about, with its comments already open.
  const fetchFocusedPost = async (postId) => {
    if (!condo?._id) return;

    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/forum/condo/${condo._id}/post/${postId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPosts([response.data.post]);
      setExpandedPostId(postId);
      setTotalPages(1);
      setCurrentPage(1);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch post:', err);
      setPosts([]);
      const status = err.response?.status;
      setError(status === 404 || status === 403 ? null : 'Failed to load post. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostsForHighlight = async (postId) => {
    if (!condo?._id) return;

    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/forum/condo/${condo._id}?postId=${postId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPosts(response.data.posts || []);
      setAudienceStats(response.data.audienceStats || EMPTY_AUDIENCE_STATS);
      if (response.data.audienceOptions) {
        const opts = response.data.audienceOptions;
        setAudienceOptions(opts);
        if (opts.residents?.length > 0) {
          setResidentNameMap(prev => {
            const map = { ...prev };
            opts.residents.forEach(r => { map[r._id] = r.name; });
            return map;
          });
        }
      }
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.page || 1);
      setExpandedPostId(null);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load posts. Please try again later.');
      setAudienceStats(EMPTY_AUDIENCE_STATS);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (page = 1) => {
    if (!condo?._id) return;

    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/forum/condo/${condo._id}?page=${page}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPosts(response.data.posts || []);
      setAudienceStats(response.data.audienceStats || EMPTY_AUDIENCE_STATS);
      if (response.data.audienceOptions) {
        const opts = response.data.audienceOptions;
        setAudienceOptions(opts);
        if (opts.residents?.length > 0) {
          setResidentNameMap(prev => {
            const map = { ...prev };
            opts.residents.forEach(r => { map[r._id] = r.name; });
            return map;
          });
        }
      }
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.page || 1);
      setExpandedPostId(null);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load posts. Please try again later.');
      setAudienceStats(EMPTY_AUDIENCE_STATS);
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    if (!condo?._id) return;
    
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/tickets/condo/${condo._id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - postImages.length;
    if (remainingSlots === 0) {
      setError('Maximum 5 images allowed per post');
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    setUploadingImage(true);

    try {
      const uploadPromises = filesToUpload.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        const response = await axios.post(`${getApiBaseUrl()}/api/upload/image`, formData, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      setPostImages(prev => [...prev, ...results]);
      setError(null);
    } catch (err) {
      console.error('Failed to upload image:', err);
      setError(err.response?.data?.error || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index) => {
    setPostImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || postVideos.length >= MAX_VIDEOS) return;

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await axios.post(`${getApiBaseUrl()}/api/upload/video`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setPostVideos(prev => [...prev, response.data]);
      setError(null);
    } catch (err) {
      console.error('Failed to upload video:', err);
      setError(err.response?.data?.error || 'Failed to upload video. Please try again.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleRemoveVideo = (index) => {
    const video = postVideos[index];
    if (video?.key) deleteUploadedVideo(video.key);
    setPostVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleApplyAudience = async () => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/forum/reach`, {
        condoId: condo._id,
        audience: draftAudience
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setAppliedReachCount(response.data.reach);
    } catch (err) {
      console.error('Failed to compute reach:', err);
      setAppliedReachCount(0);
    }
    setPostAudience(draftAudience);
    setCustomPanelOpen(false);
  };

  const toggleAudienceRole = (role) => {
    setDraftAudience(prev => {
      const roles = prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role];
      return { ...prev, everyone: false, roles };
    });
  };

  const toggleAudienceUnitType = (type) => {
    setDraftAudience(prev => {
      const unitTypes = prev.unitTypes.includes(type) ? prev.unitTypes.filter(t => t !== type) : [...prev.unitTypes, type];
      return { ...prev, everyone: false, unitTypes };
    });
  };

  const toggleAudienceFeature = (feature) => {
    setDraftAudience(prev => {
      const unitFeatures = prev.unitFeatures.includes(feature) ? prev.unitFeatures.filter(f => f !== feature) : [...prev.unitFeatures, feature];
      return { ...prev, everyone: false, unitFeatures };
    });
  };

  const addAudienceUnitNumber = (num) => {
    setDraftAudience(prev => {
      if (prev.unitNumbers.includes(num)) return prev;
      return { ...prev, everyone: false, unitNumbers: [...prev.unitNumbers, num] };
    });
  };

  const removeAudienceUnitNumber = (num) => {
    setDraftAudience(prev => ({ ...prev, unitNumbers: prev.unitNumbers.filter(n => n !== num) }));
  };

  const addAudienceParkingSpot = (spot) => {
    setDraftAudience(prev => {
      if (prev.parkingSpots.includes(spot)) return prev;
      return { ...prev, everyone: false, parkingSpots: [...prev.parkingSpots, spot] };
    });
  };

  const removeAudienceParkingSpot = (spot) => {
    setDraftAudience(prev => ({ ...prev, parkingSpots: prev.parkingSpots.filter(s => s !== spot) }));
  };

  const addAudienceUserId = (resident) => {
    setDraftAudience(prev => {
      if (prev.userIds.includes(resident._id)) return prev;
      return { ...prev, everyone: false, userIds: [...prev.userIds, resident._id] };
    });
    setResidentNameMap(prev => ({ ...prev, [resident._id]: resident.name }));
  };

  const removeAudienceUserId = (id) => {
    setDraftAudience(prev => ({ ...prev, userIds: prev.userIds.filter(i => i !== id) }));
  };

  const handleMessageResident = async (author) => {
    const singleUserAudience = { ...DEFAULT_AUDIENCE, everyone: false, userIds: [author._id] };
    setResidentNameMap(prev => ({ ...prev, [author._id]: author.name }));
    let reach = 1;
    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/forum/reach`, {
        condoId: condo._id,
        audience: singleUserAudience
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      reach = response.data.reach;
    } catch (err) {
      console.error('Failed to compute reach:', err);
    }
    setAppliedReachCount(reach);
    setPostAudience(singleUserAudience);
    setDraftAudience(singleUserAudience);
    setCustomPanelOpen(false);
    setComposerExpanded(true);
    setTimeout(() => {
      postTextareaRef.current?.focus();
      postTextareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const authorMenuProps = (key, author) => {
    const isOtherUser = author?._id && user?._id && author._id.toString() !== user._id.toString();
    return {
      author,
      isOpen: authorMenuOpen === key,
      onToggle: () => setAuthorMenuOpen(prev => (prev === key ? null : key)),
      onMessage: isOtherUser ? () => { setAuthorMenuOpen(null); handleMessageResident(author); } : undefined
    };
  };

  const audienceOption = getAudienceOption(postAudience);

  const getCustomAudienceLabel = () => {
    const { roles, unitTypes, unitFeatures, unitNumbers, parkingSpots, userIds, includeCouncil } = postAudience;
    const onlyUserIds = userIds.length === 1 && !roles.length && !unitTypes.length && !unitFeatures.length
      && !unitNumbers.length && !parkingSpots.length && !includeCouncil;
    if (onlyUserIds) return residentNameMap[userIds[0]] || `Custom (${appliedReachCount})`;

    const onlyUnitNumber = unitNumbers.length === 1 && !roles.length && !unitTypes.length && !unitFeatures.length
      && !parkingSpots.length && !userIds.length && !includeCouncil;
    if (onlyUnitNumber) return `Unit ${unitNumbers[0]}`;

    return `Custom (${appliedReachCount})`;
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!postContent.trim() || submitting) return;

    setSubmitting(true);
    try {
      const postData = {
        condoId: condo._id,
        content: postContent.trim(),
        images: postImages,
        videos: postVideos,
        audience: postAudience
      };
      
      if (selectedTicket) {
        postData.ticketId = selectedTicket._id;
      }

      await axios.post(`${getApiBaseUrl()}/api/forum`, postData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      track('forum_post_created', { images: postImages.length, videos: postVideos.length });
      setPostContent('');
      setPostAudience(DEFAULT_AUDIENCE);
      setCustomPanelOpen(false);
      setDraftAudience({ ...DEFAULT_AUDIENCE, everyone: false });
      setAppliedReachCount(0);
      setUnitNumberInput('');
      setParkingSpotInput('');
      setResidentInput('');
      setPostImages([]);
      setPostVideos([]);
      setSelectedTicket(null);
      setTicketSearch('');
      setComposerExpanded(false);
      if (postTextareaRef.current) {
        postTextareaRef.current.style.height = 'auto';
      }
      if (focusPostId) {
        navigate(`/${condoSlug}/forum`);
      } else {
        await fetchPosts();
      }
    } catch (err) {
      console.error('Failed to create post:', err);
      setError(err.response?.data?.error || 'Failed to create post. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTicketSearch = (value) => {
    setTicketSearch(value);
    if (value.trim().length > 0) {
      setShowTicketSearch(true);
    } else {
      setShowTicketSearch(false);
    }
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setTicketSearch('');
    setShowTicketSearch(false);
  };

  const filteredTickets = tickets.filter(ticket => {
    if (ticket.status !== 'open') return false;
    const searchLower = ticketSearch.toLowerCase();
    return ticket.title.toLowerCase().includes(searchLower) ||
           (ticket.ticketNumber && ticket.ticketNumber.toLowerCase().includes(searchLower));
  }).slice(0, 5);

  const handleSubmitComment = async (e, postId) => {
    e.preventDefault();
    if (!commentContent.trim() || submittingComment) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/forum/${postId}/comments`, {
        content: commentContent.trim(),
        images: commentImages,
        videos: commentVideos
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      track('forum_comment_created');
      replacePostInState(response.data.post);
      setCommentContent('');
      setCommentImages([]);
      setCommentVideos([]);
      if (commentTextareaRef.current) {
        commentTextareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
      setError(err.response?.data?.error || 'Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const togglePost = (postId) => {
    commentVideos.forEach(v => v.key && deleteUploadedVideo(v.key));
    if (expandedPostId === postId) {
      setExpandedPostId(null);
      setCommentContent('');
      setCommentImages([]);
      setCommentVideos([]);
      setEditingCommentId(null);
    } else {
      setExpandedPostId(postId);
      setCommentContent('');
      setCommentImages([]);
      setCommentVideos([]);
      setEditingCommentId(null);
    }
  };

  const REACTIONS = [
    { type: 'thumbsup', emoji: '👍', label: 'Thumbs Up' },
    { type: 'heart', emoji: '❤️', label: 'Heart' },
    { type: 'surprised', emoji: '😮', label: 'Surprised' },
    { type: 'sad', emoji: '😢', label: 'Sad' },
  ];

  const replacePostInState = (updatedPost) => {
    setPosts(prevPosts => prevPosts.map(post => post._id === updatedPost._id ? updatedPost : post));
  };

  const getOptimisticPostReactionState = (post, reactionType) => {
    const currentCounts = { ...(post.reactionCounts || { heart: 0, sad: 0, thumbsup: 0, surprised: 0 }) };
    const currentUserReaction = post.userReaction;

    if (currentUserReaction) {
      currentCounts[currentUserReaction] = Math.max(0, (currentCounts[currentUserReaction] || 0) - 1);
    }
    let newUserReaction = null;
    if (currentUserReaction !== reactionType) {
      currentCounts[reactionType] = (currentCounts[reactionType] || 0) + 1;
      newUserReaction = reactionType;
    }

    return { ...post, reactionCounts: currentCounts, userReaction: newUserReaction };
  };

  const getOptimisticCommentReactionState = (post, commentId, reactionType) => ({
    ...post,
    comments: (post.comments || []).map(comment => {
      if (comment._id !== commentId) return comment;
      const currentCounts = { ...(comment.reactionCounts || { heart: 0, sad: 0, thumbsup: 0, surprised: 0 }) };
      const currentUserReaction = comment.userReaction;
      if (currentUserReaction) {
        currentCounts[currentUserReaction] = Math.max(0, (currentCounts[currentUserReaction] || 0) - 1);
      }
      let newUserReaction = null;
      if (currentUserReaction !== reactionType) {
        currentCounts[reactionType] = (currentCounts[reactionType] || 0) + 1;
        newUserReaction = reactionType;
      }
      return { ...comment, reactionCounts: currentCounts, userReaction: newUserReaction };
    })
  });

  const handlePostReaction = async (postId, reactionType) => {
    const loadingKey = `post-${postId}`;
    if (reactionLoading[loadingKey]) return;

    const previousPost = posts.find(p => p._id === postId);
    if (!previousPost) return;

    replacePostInState(getOptimisticPostReactionState(previousPost, reactionType));
    setReactionPickerOpen(null);
    setReactionLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/forum/${postId}/react`, { reactionType }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      track('forum_reacted', { on: 'post' });
      replacePostInState(response.data.post);
      setError(null);
    } catch (err) {
      replacePostInState(previousPost);
      console.error('Failed to update post reaction:', err);
      setError(err.response?.data?.error || 'Failed to update reaction. Please try again.');
    } finally {
      setReactionLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleCommentReaction = async (postId, commentId, reactionType) => {
    const loadingKey = `comment-${commentId}`;
    if (reactionLoading[loadingKey]) return;

    const previousPost = posts.find(p => p._id === postId);
    if (!previousPost) return;

    replacePostInState(getOptimisticCommentReactionState(previousPost, commentId, reactionType));
    setReactionPickerOpen(null);
    setReactionLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await axios.post(`${getApiBaseUrl()}/api/forum/${postId}/comments/${commentId}/react`, { reactionType }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      track('forum_reacted', { on: 'comment' });
      replacePostInState(response.data.post);
      setError(null);
    } catch (err) {
      replacePostInState(previousPost);
      console.error('Failed to update comment reaction:', err);
      setError(err.response?.data?.error || 'Failed to update reaction. Please try again.');
    } finally {
      setReactionLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleStartEdit = (post) => {
    setEditingPostId(post._id);
    setEditContent(post.content);
    setEditImages(post.images || []);
    setEditVideos(post.videos || []);
    setOriginalEditVideos(post.videos || []);
    setTimeout(() => autoResizeTextarea(editTextareaRef.current), 0);
  };

  const handleCancelEdit = () => {
    // Only delete videos uploaded (but never saved) during this edit session —
    // videos already attached to the persisted post must stay untouched.
    const originalKeys = new Set(originalEditVideos.map(v => v.key));
    editVideos.filter(v => v.key && !originalKeys.has(v.key)).forEach(v => deleteUploadedVideo(v.key));
    setEditingPostId(null);
    setEditContent('');
    setEditImages([]);
    setEditVideos([]);
    setOriginalEditVideos([]);
  };

  const handleEditImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - editImages.length;
    if (remainingSlots === 0) return;

    const filesToUpload = files.slice(0, remainingSlots);
    setUploadingEditImage(true);

    try {
      const results = await Promise.all(filesToUpload.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        const response = await axios.post(`${getApiBaseUrl()}/api/upload/image`, formData, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
      }));
      setEditImages(prev => [...prev, ...results]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingEditImage(false);
    }
  };

  const handleRemoveEditImage = (index) => {
    setEditImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditVideoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || editVideos.length >= MAX_VIDEOS) return;

    setUploadingEditVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await axios.post(`${getApiBaseUrl()}/api/upload/video`, formData, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
      });
      setEditVideos(prev => [...prev, response.data]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload video. Please try again.');
    } finally {
      setUploadingEditVideo(false);
    }
  };

  const handleRemoveEditVideo = (index) => {
    const video = editVideos[index];
    // If this video isn't one of the post's saved videos, it was uploaded
    // during this edit session and never persisted — delete it now, or it's
    // orphaned. Videos already attached to the post are left alone; the
    // PUT on Save (or simply cancelling) handles those.
    const isOriginal = originalEditVideos.some(v => v.key === video?.key);
    if (video?.key && !isOriginal) deleteUploadedVideo(video.key);
    setEditVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEdit = async (postId) => {
    if (!editContent.trim() || savingEdit) return;

    setSavingEdit(true);
    try {
      const response = await axios.put(`${getApiBaseUrl()}/api/forum/${postId}`, {
        content: editContent.trim(),
        images: editImages,
        videos: editVideos
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      replacePostInState(response.data.post);
      setEditingPostId(null);
      setEditContent('');
      setEditImages([]);
      setEditVideos([]);
      setOriginalEditVideos([]);
      setError(null);
    } catch (err) {
      console.error('Failed to edit post:', err);
      setError(err.response?.data?.error || 'Failed to save edit. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleStartCommentEdit = (comment) => {
    setEditingCommentId(comment._id);
    setEditCommentContent(comment.content);
    setEditCommentImages(comment.images || []);
    setEditCommentVideos(comment.videos || []);
    setOriginalEditCommentVideos(comment.videos || []);
  };

  const handleCancelCommentEdit = () => {
    const originalKeys = new Set(originalEditCommentVideos.map(v => v.key));
    editCommentVideos.filter(v => v.key && !originalKeys.has(v.key)).forEach(v => deleteUploadedVideo(v.key));
    setEditingCommentId(null);
    setEditCommentContent('');
    setEditCommentImages([]);
    setEditCommentVideos([]);
    setOriginalEditCommentVideos([]);
  };

  const handleCommentEditImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - editCommentImages.length;
    if (remainingSlots === 0) return;

    const filesToUpload = files.slice(0, remainingSlots);
    setUploadingCommentEditImage(true);

    try {
      const results = await Promise.all(filesToUpload.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        const response = await axios.post(`${getApiBaseUrl()}/api/upload/image`, formData, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
      }));
      setEditCommentImages(prev => [...prev, ...results]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingCommentEditImage(false);
    }
  };

  const handleRemoveCommentEditImage = (index) => {
    setEditCommentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCommentEditVideoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || editCommentVideos.length >= MAX_VIDEOS) return;

    setUploadingCommentEditVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await axios.post(`${getApiBaseUrl()}/api/upload/video`, formData, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
      });
      setEditCommentVideos(prev => [...prev, response.data]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload video. Please try again.');
    } finally {
      setUploadingCommentEditVideo(false);
    }
  };

  const handleRemoveCommentEditVideo = (index) => {
    const video = editCommentVideos[index];
    const isOriginal = originalEditCommentVideos.some(v => v.key === video?.key);
    if (video?.key && !isOriginal) deleteUploadedVideo(video.key);
    setEditCommentVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveCommentEdit = async (postId, commentId) => {
    if (!editCommentContent.trim() || savingCommentEdit) return;

    setSavingCommentEdit(true);
    try {
      const response = await axios.put(`${getApiBaseUrl()}/api/forum/${postId}/comments/${commentId}`, {
        content: editCommentContent.trim(),
        images: editCommentImages,
        videos: editCommentVideos
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      replacePostInState(response.data.post);
      setEditingCommentId(null);
      setEditCommentContent('');
      setEditCommentImages([]);
      setEditCommentVideos([]);
      setOriginalEditCommentVideos([]);
      setError(null);
    } catch (err) {
      console.error('Failed to edit comment:', err);
      setError(err.response?.data?.error || 'Failed to save comment edit. Please try again.');
    } finally {
      setSavingCommentEdit(false);
    }
  };

  const handleCommentImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES - commentImages.length;
    if (remainingSlots === 0) return;

    const filesToUpload = files.slice(0, remainingSlots);
    setUploadingCommentImage(true);

    try {
      const results = await Promise.all(filesToUpload.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        const response = await axios.post(`${getApiBaseUrl()}/api/upload/image`, formData, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
      }));
      setCommentImages(prev => [...prev, ...results]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingCommentImage(false);
    }
  };

  const handleRemoveCommentImage = (index) => {
    setCommentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCommentVideoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || commentVideos.length >= MAX_VIDEOS) return;

    setUploadingCommentVideo(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      const response = await axios.post(`${getApiBaseUrl()}/api/upload/video`, formData, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
      });
      setCommentVideos(prev => [...prev, response.data]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload video. Please try again.');
    } finally {
      setUploadingCommentVideo(false);
    }
  };

  const handleRemoveCommentVideo = (index) => {
    const video = commentVideos[index];
    if (video?.key) deleteUploadedVideo(video.key);
    setCommentVideos(prev => prev.filter((_, i) => i !== index));
  };

  // A focused post that failed to load already shows the error banner; the
  // "isn't available" card would otherwise claim it was removed during an outage.
  const showEmptyState = !(focusPostId && error);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading {focusPostId ? 'post' : 'forum'}...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded-lg">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="ml-2 text-sm underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {focusPostId ? (
        <Link
          to={`/${condoSlug}/forum`}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          ← Back to forum
        </Link>
      ) : (
      <div className="bg-card border border-border rounded-lg shadow-md px-4 py-2.5 sm:px-5 sm:py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary sm:hidden">
              <Users className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <p className="text-base font-medium text-foreground">Community reach</p>
              <p className="hidden text-sm text-muted-foreground sm:block">
                Active residents you can connect with in this residence forum
              </p>
            </div>
          </div>
          <div data-testid="community-reach-compact" className="rounded-full bg-primary/10 px-3 py-1 text-sm sm:hidden">
            <span className="font-semibold text-foreground">{audienceStats.totalResidents}</span>
            <span className="text-muted-foreground"> residents</span>
          </div>
          <div data-testid="community-reach-breakdown" className="hidden sm:flex sm:flex-wrap sm:justify-end sm:gap-2">
            <div className="rounded-lg bg-primary/10 px-3 py-2 min-w-[120px]">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Residents</div>
              <div className="text-lg font-semibold text-foreground">{audienceStats.totalResidents}</div>
            </div>
            <div className="rounded-lg bg-muted/40 px-3 py-2 min-w-[120px]">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Owners</div>
              <div className="text-lg font-semibold text-foreground">{audienceStats.owners}</div>
            </div>
            <div className="rounded-lg bg-muted/40 px-3 py-2 min-w-[120px]">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Tenants</div>
              <div className="text-lg font-semibold text-foreground">{audienceStats.tenants}</div>
            </div>
            <div className="rounded-lg bg-muted/40 px-3 py-2 min-w-[120px]">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Council</div>
              <div className="text-lg font-semibold text-foreground">{audienceStats.councilMembers}</div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* On the focused page the composer only appears once "Message resident" opens it. */}
      {(!focusPostId || composerExpanded) && (
      <div className="bg-card border border-border rounded-lg shadow-md overflow-visible">
        {!composerExpanded ? (
          <button
            onClick={() => setComposerExpanded(true)}
            className="w-full p-4 text-left text-muted-foreground bg-input rounded-lg hover:bg-muted/70 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="text-xl">✏️</span>
              <span>Share something with your community...</span>
            </span>
          </button>
        ) : (
          <form onSubmit={handleSubmitPost} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Who can see this post?
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {AUDIENCE_PRESETS.map(preset => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => { setPostAudience(preset.audience); setCustomPanelOpen(false); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                      audienceOption === preset.key && !customPanelOpen
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:border-foreground'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setDraftAudience(audienceOption === 'custom' ? { ...postAudience } : { ...DEFAULT_AUDIENCE, everyone: false });
                    setCustomPanelOpen(true);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    audienceOption === 'custom' && !customPanelOpen
                      ? 'bg-primary text-primary-foreground border-primary'
                      : customPanelOpen
                      ? 'border-primary text-primary bg-primary/10'
                      : 'border-dashed border-border text-muted-foreground hover:border-foreground hover:text-foreground'
                  }`}
                >
                  {audienceOption === 'custom' && !customPanelOpen
                    ? getCustomAudienceLabel()
                    : 'Custom'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                {AUDIENCE_DESCRIPTIONS[customPanelOpen ? 'custom' : audienceOption]}
              </p>
              {customPanelOpen && (
                <div className="space-y-3 p-3 border border-border rounded-lg bg-muted/20">
                  <div>
                    <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Roles</p>
                    <div className="flex flex-wrap gap-2">
                      {['owner', 'tenant'].map(role => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => toggleAudienceRole(role)}
                          className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                            draftAudience.roles.includes(role)
                              ? 'bg-primary/20 text-primary border-primary/50'
                              : 'bg-background text-muted-foreground border-border hover:border-foreground'
                          }`}
                        >
                          {role === 'owner' ? '🏠 Owners' : '🔑 Tenants'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {audienceOptions.unitTypes.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Unit types</p>
                      <div className="flex flex-wrap gap-2">
                        {audienceOptions.unitTypes.map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => toggleAudienceUnitType(type)}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              draftAudience.unitTypes.includes(type)
                                ? 'bg-primary/20 text-primary border-primary/50'
                                : 'bg-background text-muted-foreground border-border hover:border-foreground'
                            }`}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {audienceOptions.unitFeatures.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Features</p>
                      <div className="flex flex-wrap gap-2">
                        {audienceOptions.unitFeatures.map(feature => (
                          <button
                            key={feature}
                            type="button"
                            onClick={() => toggleAudienceFeature(feature)}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              draftAudience.unitFeatures.includes(feature)
                                ? 'bg-primary/20 text-primary border-primary/50'
                                : 'bg-background text-muted-foreground border-border hover:border-foreground'
                            }`}
                          >
                            {feature}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {audienceOptions.unitNumbers.length > 0 && (
                  <div className="relative">
                    <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Units</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {draftAudience.unitNumbers.map(num => (
                        <span key={num} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-primary/20 text-primary border border-primary/50">
                          Unit {num}
                          <button type="button" onClick={() => removeAudienceUnitNumber(num)} className="hover:text-destructive ml-0.5 leading-none">&times;</button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={unitNumberInput}
                      onChange={(e) => setUnitNumberInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const matches = audienceOptions.unitNumbers.filter(
                            n => !draftAudience.unitNumbers.includes(n) && String(n).includes(unitNumberInput)
                          );
                          if (matches.length === 1) {
                            e.preventDefault();
                            addAudienceUnitNumber(matches[0]);
                            setUnitNumberInput('');
                          }
                        }
                      }}
                      placeholder="Search unit numbers..."
                      className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                    />
                    {unitNumberInput.length > 0 && (() => {
                      const matches = audienceOptions.unitNumbers.filter(
                        n => !draftAudience.unitNumbers.includes(n) && String(n).includes(unitNumberInput)
                      );
                      return (
                        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {matches.length > 0 ? matches.map(num => (
                            <button
                              key={num}
                              type="button"
                              onClick={() => { addAudienceUnitNumber(num); setUnitNumberInput(''); }}
                              className="w-full p-2.5 text-left text-sm text-foreground hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                            >
                              Unit {num}
                            </button>
                          )) : (
                            <div className="p-2.5 text-sm text-muted-foreground">No matching units</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                  {audienceOptions.parkingSpots.length > 0 && (
                  <div className="relative">
                    <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Parking spots</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {draftAudience.parkingSpots.map(spot => (
                        <span key={spot} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-primary/20 text-primary border border-primary/50">
                          {spot}
                          <button type="button" onClick={() => removeAudienceParkingSpot(spot)} className="hover:text-destructive ml-0.5 leading-none">&times;</button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={parkingSpotInput}
                      onChange={(e) => setParkingSpotInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const matches = audienceOptions.parkingSpots.filter(
                            s => !draftAudience.parkingSpots.includes(s) && s.toLowerCase().includes(parkingSpotInput.toLowerCase())
                          );
                          if (matches.length === 1) {
                            e.preventDefault();
                            addAudienceParkingSpot(matches[0]);
                            setParkingSpotInput('');
                          }
                        }
                      }}
                      placeholder="Search parking spots..."
                      className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                    />
                    {parkingSpotInput.length > 0 && (() => {
                      const matches = audienceOptions.parkingSpots.filter(
                        s => !draftAudience.parkingSpots.includes(s) && s.toLowerCase().includes(parkingSpotInput.toLowerCase())
                      );
                      return (
                        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {matches.length > 0 ? matches.map(spot => (
                            <button
                              key={spot}
                              type="button"
                              onClick={() => { addAudienceParkingSpot(spot); setParkingSpotInput(''); }}
                              className="w-full p-2.5 text-left text-sm text-foreground hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                            >
                              {spot}
                            </button>
                          )) : (
                            <div className="p-2.5 text-sm text-muted-foreground">No matching parking spots</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                  {audienceOptions.residents.length > 0 && (
                  <div className="relative">
                    <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Residents</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {draftAudience.userIds.map(id => (
                        <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-primary/20 text-primary border border-primary/50">
                          {residentNameMap[id] || id}
                          <button type="button" onClick={() => removeAudienceUserId(id)} className="hover:text-destructive ml-0.5 leading-none">&times;</button>
                        </span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={residentInput}
                      onChange={(e) => setResidentInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const matches = audienceOptions.residents.filter(
                            r => !draftAudience.userIds.includes(r._id) && r.name.toLowerCase().includes(residentInput.toLowerCase())
                          );
                          if (matches.length === 1) {
                            e.preventDefault();
                            addAudienceUserId(matches[0]);
                            setResidentInput('');
                          }
                        }
                      }}
                      placeholder="Search residents..."
                      className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                    />
                    {residentInput.length > 0 && (() => {
                      const matches = audienceOptions.residents.filter(
                        r => !draftAudience.userIds.includes(r._id) && r.name.toLowerCase().includes(residentInput.toLowerCase())
                      );
                      return (
                        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {matches.length > 0 ? matches.map(r => (
                            <button
                              key={r._id}
                              type="button"
                              onClick={() => { addAudienceUserId(r); setResidentInput(''); }}
                              className="w-full p-2.5 text-left text-sm text-foreground hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                            >
                              {r.name}{r.unit != null ? ` · Unit ${r.unit}` : ''}
                            </button>
                          )) : (
                            <div className="p-2.5 text-sm text-muted-foreground">No matching residents</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground tracking-wide mb-1.5">Also include</p>
                    <button
                      type="button"
                      onClick={() => setDraftAudience(prev => ({ ...prev, includeCouncil: !prev.includeCouncil }))}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        draftAudience.includeCouncil
                          ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/50'
                          : 'bg-background text-muted-foreground border-border hover:border-foreground'
                      }`}
                    >
                      👥 Council
                    </button>
                  </div>
                  <div className="flex justify-end gap-2 pt-1 border-t border-border">
                    <button
                      type="button"
                      onClick={() => setCustomPanelOpen(false)}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyAudience}
                      className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Content</label>
              <MentionTextarea
                ref={postTextareaRef}
                value={postContent}
                maxLength={POST_CHAR_LIMIT}
                candidates={composerMentionCandidates}
                onValueChange={(value, el) => {
                  setPostContent(value);
                  autoResizeTextarea(el);
                }}
                placeholder="What's on your mind? Type @ to mention someone"
                className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[100px] overflow-hidden"
                rows={4}
                autoFocus
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span className={postContent.length > 0 && postContent.length < POST_MIN_CHARS ? 'text-destructive' : ''}>
                  {postContent.length < POST_MIN_CHARS ? `${POST_MIN_CHARS - postContent.length} more characters needed` : ''}
                </span>
                <span>{postContent.length}/{POST_CHAR_LIMIT}</span>
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-foreground mb-2">
                Reference a ticket (optional)
              </label>
              {selectedTicket ? (
                <div className="p-3 border border-border rounded-lg bg-muted/30">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">
                          🎫 {selectedTicket.ticketNumber && `${selectedTicket.ticketNumber}: `}{selectedTicket.title}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{selectedTicket.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedTicket(null)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={ticketSearch}
                    onChange={(e) => handleTicketSearch(e.target.value)}
                    placeholder="Search tickets..."
                    className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  {showTicketSearch && ticketSearch.length > 1 && (
                    <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredTickets.length > 0 ? (
                        filteredTickets.map(ticket => (
                          <button
                            key={ticket._id}
                            type="button"
                            onClick={() => handleSelectTicket(ticket)}
                            className="w-full p-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-b-0"
                          >
                            <div className="font-medium text-sm text-foreground mb-1">
                              🎫 {ticket.ticketNumber && `${ticket.ticketNumber}: `}{ticket.title}
                            </div>
                            <div className="text-xs text-muted-foreground line-clamp-2">{ticket.description}</div>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-muted-foreground">No tickets found</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {postImages.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {postImages.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image.url}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-border"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {postVideos.length > 0 && (
              <div className="space-y-2">
                {postVideos.map((video, index) => (
                  <div key={index} className="relative group">
                    <video
                      src={video.url}
                      controls
                      className="w-full max-h-64 rounded-lg border border-border bg-black"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveVideo(index)}
                      className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 items-center flex-wrap">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  disabled={postImages.length >= MAX_IMAGES || uploadingImage}
                  className="hidden"
                />
                <span className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50">
                  <span>📷</span>
                  <span>{uploadingImage ? 'Uploading...' : `Add Image (${postImages.length}/${MAX_IMAGES})`}</span>
                </span>
              </label>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  disabled={postVideos.length >= MAX_VIDEOS || uploadingVideo}
                  className="hidden"
                />
                <span className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50">
                  <span>🎥</span>
                  <span>{uploadingVideo ? 'Uploading & compressing...' : `Add Video (${postVideos.length}/${MAX_VIDEOS})`}</span>
                </span>
              </label>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setComposerExpanded(false);
                  setPostContent('');
                  setPostAudience(DEFAULT_AUDIENCE);
                  setCustomPanelOpen(false);
                  setDraftAudience({ ...DEFAULT_AUDIENCE, everyone: false });
                  setAppliedReachCount(0);
                  setUnitNumberInput('');
                  setParkingSpotInput('');
                  setResidentInput('');
                  setPostImages([]);
                  postVideos.forEach(v => v.key && deleteUploadedVideo(v.key));
                  setPostVideos([]);
                }}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!postContent.trim() || postContent.length < POST_MIN_CHARS || submitting || uploadingImage || uploadingVideo}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </form>
        )}
      </div>
      )}

      {posts.length === 0 ? (
        showEmptyState && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          {focusPostId ? (
            <>
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-foreground text-lg font-semibold">This post isn't available</p>
              <p className="text-muted-foreground mt-2">
                It may have been removed, or it isn't shared with you.
              </p>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">🦗</div>
              <p className="text-foreground text-lg font-semibold">It's quiet here...</p>
              <p className="text-muted-foreground mt-2">
                Be the first to start a conversation in your community!
              </p>
            </>
          )}
        </div>
        )
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const isExpanded = expandedPostId === post._id;
            const postReactionLoadingKey = `post-${post._id}`;
            const postReactionIsLoading = reactionLoading[postReactionLoadingKey];

            return (
              <div
                key={post._id}
                ref={el => postRefs.current[post._id] = el}
                className="bg-card border border-border rounded-lg shadow-md overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                      <AuthorMenu {...authorMenuProps(`post-${post._id}`, post.user)} />
                      <span className="text-muted-foreground" aria-hidden="true">·</span>
                      <span className="text-muted-foreground">
                        {formatRelativeTime(post.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?._id && post.user?._id && post.user._id.toString() === user._id.toString() && editingPostId !== post._id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(post);
                          }}
                          title="Edit post"
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted/50 transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                  <PostAudience post={post} viewerId={user?._id} />
                  {editingPostId === post._id ? (
                    <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                      <MentionTextarea
                        ref={editTextareaRef}
                        value={editContent}
                        maxLength={POST_CHAR_LIMIT}
                        candidates={postMentionCandidates[post._id]}
                        onValueChange={(value, el) => {
                          setEditContent(value);
                          autoResizeTextarea(el);
                        }}
                        className="w-full p-3 border border-border rounded-lg bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[80px] overflow-hidden text-sm"
                        autoFocus
                      />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className={editContent.length > 0 && editContent.length < POST_MIN_CHARS ? 'text-destructive' : ''}>
                          {editContent.length < POST_MIN_CHARS ? `${POST_MIN_CHARS - editContent.length} more characters needed` : ''}
                        </span>
                        <span>{editContent.length}/{POST_CHAR_LIMIT}</span>
                      </div>
                      {editImages.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {editImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <img src={image.url} alt={`${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-border" />
                              <button
                                type="button"
                                onClick={() => handleRemoveEditImage(index)}
                                className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {editVideos.length > 0 && (
                        <div className="space-y-2">
                          {editVideos.map((video, index) => (
                            <div key={index} className="relative group">
                              <video src={video.url} controls className="w-full max-h-56 rounded-lg border border-border bg-black" />
                              <button
                                type="button"
                                onClick={() => handleRemoveEditVideo(index)}
                                className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 items-center justify-between">
                        <div className="flex gap-2 flex-wrap">
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" multiple onChange={handleEditImageUpload} disabled={editImages.length >= MAX_IMAGES || uploadingEditImage} className="hidden" />
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors">
                              <span>📷</span>
                              <span>{uploadingEditImage ? 'Uploading...' : `Images (${editImages.length}/${MAX_IMAGES})`}</span>
                            </span>
                          </label>
                          <label className="cursor-pointer">
                            <input type="file" accept="video/*" onChange={handleEditVideoUpload} disabled={editVideos.length >= MAX_VIDEOS || uploadingEditVideo} className="hidden" />
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors">
                              <span>🎥</span>
                              <span>{uploadingEditVideo ? 'Uploading...' : `Video (${editVideos.length}/${MAX_VIDEOS})`}</span>
                            </span>
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(post._id)}
                            disabled={!editContent.trim() || editContent.length < POST_MIN_CHARS || savingEdit || uploadingEditImage || uploadingEditVideo}
                            className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {savingEdit ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-foreground whitespace-pre-wrap"><MentionText text={post.content} mentions={post.mentions} /></p>
                      {post.editedAt && (
                        <p className="text-xs text-muted-foreground mt-1">(edited {formatRelativeTime(post.editedAt)})</p>
                      )}
                    </div>
                  )}
                  {post.ticket && (
                    <div 
                      className="mt-3 p-3 border border-border rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${condoSlug}/tickets?ticket=${post.ticket._id}`);
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">🎫</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-foreground mb-1">
                            {post.ticket.ticketNumber && `${post.ticket.ticketNumber}: `}{post.ticket.title}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">{post.ticket.description}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {post.images && post.images.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {post.images.map((image, index) => (
                        <img
                          key={index}
                          src={image.url}
                          alt={`${index + 1}`}
                          className="w-full rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightbox({ images: post.images, index });
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {post.videos && post.videos.length > 0 && (
                    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                      {post.videos.map((video, index) => (
                        <video
                          key={index}
                          src={video.url}
                          controls
                          className="w-full max-h-96 rounded-lg border border-border bg-black"
                        />
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => togglePost(post._id)}
                    className="mt-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span>💬</span>
                    <span>{post.comments?.length || 0} comment{post.comments?.length !== 1 ? 's' : ''}</span>
                    <span className="ml-2 text-primary">{isExpanded ? '▼ Hide comments' : '▶ Show comments'}</span>
                  </button>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <div className="relative">
                      <button
                        type="button"
                        aria-label="React to post"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReactionPickerOpen(prev => prev === `post-${post._id}` ? null : `post-${post._id}`);
                        }}
                        disabled={postReactionIsLoading}
                        className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full border text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${post.userReaction ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
                      >
                        <span>{post.userReaction ? REACTIONS.find(r => r.type === post.userReaction)?.emoji : '🙂'}</span>
                        <span className="text-xs">{post.userReaction ? REACTIONS.find(r => r.type === post.userReaction)?.label : 'React'}</span>
                      </button>
                      {reactionPickerOpen === `post-${post._id}` && (
                        <div className="absolute bottom-full mb-2 left-0 z-50 flex gap-1 bg-card border border-border rounded-full shadow-lg px-2 py-1.5">
                          {REACTIONS.map(r => (
                            <button
                              key={r.type}
                              type="button"
                              title={r.label}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePostReaction(post._id, r.type);
                              }}
                              className={`text-xl w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-125 ${post.userReaction === r.type ? 'bg-primary/20' : 'hover:bg-muted/50'}`}
                            >
                              {r.emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {REACTIONS.map(r => {
                      const count = post.reactionCounts?.[r.type] || 0;
                      if (count === 0) return null;
                      return (
                        <span key={r.type} className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/40 rounded-full px-2 py-1">
                          <span>{r.emoji}</span>
                          <span>{count}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-border" onClick={(e) => e.stopPropagation()}>
                    <div className="p-4 bg-muted/20">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-foreground">
                          Comments ({post.comments?.length || 0})
                        </h4>
                        <button
                          onClick={() => togglePost(post._id)}
                          className="text-sm text-muted-foreground hover:text-foreground"
                        >
                          Collapse
                        </button>
                      </div>

                      {post.comments && post.comments.length > 0 ? (
                        <div className="space-y-4 sm:space-y-3 mb-4">
                          {post.comments.map((comment) => {
                            const commentReactionLoadingKey = `comment-${comment._id}`;
                            const commentReactionIsLoading = reactionLoading[commentReactionLoadingKey];

                            return (
                              <div
                                key={comment._id}
                                className="bg-card p-4 sm:p-3 rounded-lg border border-border"
                              >
                                <div className="flex items-center justify-between gap-3 mb-3 sm:mb-1">
                                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                                    <AuthorMenu {...authorMenuProps(`comment-${comment._id}`, comment.user)} />
                                    <span className="text-muted-foreground" aria-hidden="true">·</span>
                                    <span className="text-muted-foreground">
                                      {formatRelativeTime(comment.createdAt)}
                                      {comment.editedAt && <span className="ml-1">(edited)</span>}
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    {user?._id && comment.user?._id && comment.user._id.toString() === user._id.toString() && editingCommentId !== comment._id && (
                                      <button
                                        onClick={() => handleStartCommentEdit(comment)}
                                        title="Edit comment"
                                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 sm:px-1.5 sm:py-0.5 rounded hover:bg-muted/50 transition-colors"
                                      >
                                        <Pencil className="w-3 h-3" aria-hidden="true" />
                                        Edit
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {editingCommentId === comment._id ? (
                                  <div className="space-y-2">
                                    <MentionTextarea
                                      value={editCommentContent}
                                      maxLength={COMMENT_CHAR_LIMIT}
                                      candidates={postMentionCandidates[post._id]}
                                      onValueChange={setEditCommentContent}
                                      className="w-full p-2 border border-border rounded-lg bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm min-h-[60px]"
                                      autoFocus
                                    />
                                    <div className="text-right text-xs text-muted-foreground">{editCommentContent.length}/{COMMENT_CHAR_LIMIT}</div>
                                    {editCommentImages.length > 0 && (
                                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {editCommentImages.map((image, index) => (
                                          <div key={index} className="relative group">
                                            <img src={image.url} alt={`${index + 1}`} className="w-full h-20 object-cover rounded-lg border border-border" />
                                            <button type="button" onClick={() => handleRemoveCommentEditImage(index)} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {editCommentVideos.length > 0 && (
                                      <div className="space-y-2">
                                        {editCommentVideos.map((video, index) => (
                                          <div key={index} className="relative group">
                                            <video src={video.url} controls className="w-full max-h-48 rounded-lg border border-border bg-black" />
                                            <button type="button" onClick={() => handleRemoveCommentEditVideo(index)} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                      <div className="flex gap-2 flex-wrap">
                                        <label className="cursor-pointer">
                                          <input type="file" accept="image/*" multiple onChange={handleCommentEditImageUpload} disabled={editCommentImages.length >= MAX_IMAGES || uploadingCommentEditImage} className="hidden" />
                                          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors">
                                            <span>📷</span>
                                            <span>{uploadingCommentEditImage ? 'Uploading...' : `Images (${editCommentImages.length}/${MAX_IMAGES})`}</span>
                                          </span>
                                        </label>
                                        <label className="cursor-pointer">
                                          <input type="file" accept="video/*" onChange={handleCommentEditVideoUpload} disabled={editCommentVideos.length >= MAX_VIDEOS || uploadingCommentEditVideo} className="hidden" />
                                          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors">
                                            <span>🎥</span>
                                            <span>{uploadingCommentEditVideo ? 'Uploading...' : `Video (${editCommentVideos.length}/${MAX_VIDEOS})`}</span>
                                          </span>
                                        </label>
                                      </div>
                                      <div className="flex justify-end gap-2">
                                        <button type="button" onClick={handleCancelCommentEdit} className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                                        <button
                                          type="button"
                                          onClick={() => handleSaveCommentEdit(post._id, comment._id)}
                                          disabled={!editCommentContent.trim() || savingCommentEdit || uploadingCommentEditImage || uploadingCommentEditVideo}
                                          className="px-3 py-1 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                          {savingCommentEdit ? 'Saving...' : 'Save'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-foreground whitespace-pre-wrap text-sm"><MentionText text={comment.content} mentions={comment.mentions} /></p>
                                    {comment.images && comment.images.length > 0 && (
                                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {comment.images.map((image, index) => (
                                          <img key={index} src={image.url} alt={`${index + 1}`} className="w-full rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setLightbox({ images: comment.images, index })} />
                                        ))}
                                      </div>
                                    )}
                                    {comment.videos && comment.videos.length > 0 && (
                                      <div className="mt-2">
                                        {comment.videos.map((video, index) => (
                                          <video key={index} src={video.url} controls className="w-full max-h-72 rounded-lg border border-border bg-black" />
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                                {editingCommentId !== comment._id && (
                                <div className="mt-3 sm:mt-2 flex items-center gap-2 flex-wrap">
                                  <div className="relative">
                                    <button
                                      type="button"
                                      aria-label="React to comment"
                                      onClick={() => setReactionPickerOpen(prev => prev === `comment-${comment._id}` ? null : `comment-${comment._id}`)}
                                      disabled={commentReactionIsLoading}
                                      className={`inline-flex items-center gap-1 px-3 sm:px-2.5 h-9 sm:h-7 rounded-full border text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${comment.userReaction ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
                                    >
                                      <span>{comment.userReaction ? REACTIONS.find(r => r.type === comment.userReaction)?.emoji : '🙂'}</span>
                                      <span>{comment.userReaction ? REACTIONS.find(r => r.type === comment.userReaction)?.label : 'React'}</span>
                                    </button>
                                    {reactionPickerOpen === `comment-${comment._id}` && (
                                      <div className="absolute bottom-full mb-2 left-0 z-50 flex gap-1 bg-card border border-border rounded-full shadow-lg px-2 py-1.5">
                                        {REACTIONS.map(r => (
                                          <button
                                            key={r.type}
                                            type="button"
                                            title={r.label}
                                            onClick={() => handleCommentReaction(post._id, comment._id, r.type)}
                                            className={`text-xl w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-125 ${comment.userReaction === r.type ? 'bg-primary/20' : 'hover:bg-muted/50'}`}
                                          >
                                            {r.emoji}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  {REACTIONS.map(r => {
                                    const count = comment.reactionCounts?.[r.type] || 0;
                                    if (count === 0) return null;
                                    return (
                                      <span key={r.type} className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/40 rounded-full px-2 py-0.5">
                                        <span>{r.emoji}</span>
                                        <span>{count}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground mb-4">
                          No comments yet. Be the first to reply!
                        </p>
                      )}

                      <form onSubmit={(e) => handleSubmitComment(e, post._id)} className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Comment</label>
                          <MentionTextarea
                            ref={commentTextareaRef}
                            value={commentContent}
                            maxLength={COMMENT_CHAR_LIMIT}
                            candidates={postMentionCandidates[post._id]}
                            onValueChange={(value, el) => {
                              setCommentContent(value);
                              autoResizeTextarea(el);
                            }}
                            placeholder="Write a comment..."
                            className="w-full p-3 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm min-h-[60px] overflow-hidden"
                            rows={2}
                          />
                          <div className="text-right text-xs text-muted-foreground mt-1">
                            {commentContent.length}/{COMMENT_CHAR_LIMIT}
                          </div>
                        </div>
                        {commentImages.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {commentImages.map((image, index) => (
                              <div key={index} className="relative group">
                                <img src={image.url} alt={`${index + 1}`} className="w-full h-20 object-cover rounded-lg border border-border" />
                                <button type="button" onClick={() => handleRemoveCommentImage(index)} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {commentVideos.length > 0 && (
                          <div className="space-y-2">
                            {commentVideos.map((video, index) => (
                              <div key={index} className="relative group">
                                <video src={video.url} controls className="w-full max-h-56 rounded-lg border border-border bg-black" />
                                <button type="button" onClick={() => handleRemoveCommentVideo(index)} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex gap-2 flex-wrap">
                            <label className="cursor-pointer">
                              <input type="file" accept="image/*" multiple onChange={handleCommentImageUpload} disabled={commentImages.length >= MAX_IMAGES || uploadingCommentImage} className="hidden" />
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors">
                                <span>📷</span>
                                <span>{uploadingCommentImage ? 'Uploading...' : `Images (${commentImages.length}/${MAX_IMAGES})`}</span>
                              </span>
                            </label>
                            <label className="cursor-pointer">
                              <input type="file" accept="video/*" onChange={handleCommentVideoUpload} disabled={commentVideos.length >= MAX_VIDEOS || uploadingCommentVideo} className="hidden" />
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-border rounded-lg hover:bg-muted/50 transition-colors">
                                <span>🎥</span>
                                <span>{uploadingCommentVideo ? 'Uploading...' : `Video (${commentVideos.length}/${MAX_VIDEOS})`}</span>
                              </span>
                            </label>
                          </div>
                          <button
                            type="submit"
                            disabled={!commentContent.trim() || submittingComment || uploadingCommentImage || uploadingCommentVideo}
                            className="self-end px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {submittingComment ? '...' : 'Reply'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {focusPostId && (
        <div className="text-center">
          <Link
            to={`/${condoSlug}/forum`}
            className="inline-block px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
          >
            Go to the forum
          </Link>
        </div>
      )}

      {!focusPostId && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => fetchPosts(currentPage - 1)}
            disabled={currentPage <= 1}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => fetchPosts(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={(index) => setLightbox(current => ({ ...current, index }))}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function ForumPage() {
  const { postId } = useParams();

  return (
    <CondoLayout>
      {({ condo }) => <ForumContent condo={condo} focusPostId={postId} />}
    </CondoLayout>
  );
}

export default ForumPage;
